/**
 * Shared functionality to work with to TVSpot.overlay_options and its
 * associated TVSpotEntry.overlay_data.
 *
 * Is used both to create overlay_data based on overlay_options and
 * User input as well for editing it.
 *
 * Depends on:
 *
 *   - this.spot: Defined as a data attribute in the Vue instance. If it
 *       has no value yet it can be fetched using an API method here.
 *
 *   - this.$api.retrieveContent()
 */

import i18n from '@/i18n';

import {getMainTVSpotFromBundle} from '@/utils';

// FIXME:
//
//   We are passing around OverlayOptionsField objects that carry a
//   .value attribute, however they are merely used for initial value
//   assignment inside their respective widgets, since props are not
//   reactive and reactivity with objects in general is a bit weird.
//   So we pull the 'final' overlayData directly from the widgets which
//   means they must be kept alive somewhat artifially to retain the
//   user choices (e.g. via v-show instead of v-if), which complicates
//   the development of their frontend.
//
//   It would likely be more manageable to move all this state into the
//   the store and just interface it from the widgets as needed. Since
//   that state is nested and can affect multiple parent objects (e.g.
//   many TVSpots with an EditableTVSpotTemplate during Campaign
//   activation), this is not a trivial refactoring.

/**
 * Represent a single editable field from the overlay options.
 *
 * Args:
 *
 *   name (String): The name used to identify the field.
 *
 *   data (Object): What kind of field, default value, optional UI
 *     config etc.
 *
 *   schema (Object): How input to the field should be validated.
 *
 *   value (Any): Current user-chosen value for this field. Undefined
 *     if no value has been specified.
 *
 *   required (Boolean): Whether the field needs to be specified.
 *
 *   render (Boolean): Whether this field is rendered in an individual
 *     UI element: If this is false, it indicates it is used in a
 *     widget instead.
 *
 * Attributes:
 *
 *   rules (Array[Object]): A set of simple validation rules, inferred
 *     from field specifics.
 */
export class OverlayOptionsField {
  constructor({name, data, schema, value, required, render}) {
    this.name = name;
    this.data = data;
    this.schema = schema;

    // Take the value that was passed in, otherwise a default_value if
    // we have one, otherwise fallback to an empty string that works
    // better than null for most widgets visualizing this field.
    if (value !== null) {
      if (isPriceField(this) && value === 0.0) {
        // Leave input empty for price of zero, which looks nicer. If we
        // don't handle this case here we'd apply a default instead
        // further below, which is confusing and must be avoided.
        this.value = null;
      } else {
        this.value = value;
      }
    } else {
      if (data.default_value !== null && data.default_value !== undefined) {
        this.value = data.default_value;
      } else {
        this.value = '';
      }
    }

    this.required = required;
    this.render = render;

    this.rules = getValidationRulesForField(this);
  }
}

/**
 * A warning about a specific OverlayOptionsField.
 *
 * Usually to tell the User he must change the default value, e.g. to
 * avoid using stock images in production.
 *
 * Args:
 *
 *   fieldName (String): Internal name of field.
 *
 *   fieldLabel (String): User visible field label.
 *
 *   prefix (String): The actual error message.
 */
export class OverlayOptionsFieldWarning {
  constructor({fieldName, fieldLabel, prefix}) {
    this.fieldName = fieldName;
    this.fieldLabel = fieldLabel;
    this.prefix = prefix;
  }
}

export function isHeaderField(field) {
  return field.data.kind === 'HEADER';
}

export function isTextField(field) {
  return field.data.kind === 'TEXT';
}

export function isTextAlignmentField(field) {
  return field.data.kind === 'TEXTALIGNMENT';
}

export function isColorField(field) {
  return field.data.kind === 'COLOR';
}

export function isDateField(field) {
  return field.data.kind === 'DATE';
}

export function isBooleanField(field) {
  return field.data.kind === 'BOOLEAN';
}

export function isChoiceField(field) {
  return field.data.kind === 'CHOICE';
}

export function isRangeField(field) {
  return field.data.kind === 'RANGE';
}

export function isMultilineTextField(field) {
  return isTextField(field) && field.data.multiline;
}

export function isImageField(field) {
  return field.data.kind === 'IMAGE';
}

export function isNumberField(field) {
  return field.data.kind === 'NUMBER';
}

export function isPriceField(field) {
  return field.data.kind === 'PRICE';
}

export const overlayFieldIdentifierMethodsMixin = {
  methods: {
    isBooleanField: isBooleanField,
    isChoiceField: isChoiceField,
    isColorField: isColorField,
    isDateField: isDateField,
    isHeaderField: isHeaderField,
    isImageField: isImageField,
    isMultilineTextField: isMultilineTextField,
    isNumberField: isNumberField,
    isPriceField: isPriceField,
    isRangeField: isRangeField,
    isTextAlignmentField: isTextAlignmentField,
    isTextField: isTextField,
  },
};

function getValidationRulesForField(field) {
  let rules = [];
  if (isTextField(field)) {
    const minLength = field.schema.minLength;
    if (minLength !== undefined) {
      rules.push(
        (v) => (v || '').length >= field.schema.minLength || i18n.t('tooShort')
      );
    }
    const maxLength = field.schema.maxLength;
    if (maxLength !== undefined) {
      rules.push(
        (v) => (v || '').length <= field.schema.maxLength || i18n.t('tooLong')
      );
    }
    if (isMultilineTextField(field)) {
      const maxRows = field.schema.maxRows;
      if (maxRows !== undefined) {
        rules.push(
          (v) =>
            (v || '').split('\n').length <= field.schema.maxRows ||
            i18n.t('tooManyRows').replace('{{ num }}', maxRows)
        );
      }
    }
  } else if (isNumberField(field) || isPriceField(field)) {
    const errorMessages = {
      invalid: isPriceField(field)
        ? i18n.t('invalidPrice')
        : i18n.t('invalidNumber'),

      tooHigh: isPriceField(field)
        ? i18n.t('invalidPriceTooHigh')
        : i18n.t('invalidNumberTooHigh'),

      tooLow: isPriceField(field)
        ? i18n.t('invalidPriceTooLow')
        : i18n.t('invalidNumberTooLow'),
    };
    const multipleOf = field.schema.multipleOf;

    // TODO: Add support for exclusiveMaximum.
    //   https://json-schema.org/understanding-json-schema/reference/numeric.html#range
    rules = [
      (v) =>
        ensureDotSeparator(v) <= field.schema.maximum || errorMessages.tooHigh,
      (v) =>
        hasEqualOrLessOrZeroValueDecimals(v, multipleOf) ||
        errorMessages.invalid,
    ];
    if (field.schema.exclusiveMinimum !== undefined) {
      rules.push(
        (v) =>
          ensureDotSeparator(v) > field.schema.exclusiveMinimum ||
          errorMessages.tooLow
      );
    }
    if (field.schema.minimum !== undefined) {
      rules.push(
        (v) =>
          ensureDotSeparator(v) >= field.schema.minimum || errorMessages.tooLow
      );
    }
  } else if (isChoiceField(field)) {
    // If using a non-choosable value, make the user pick an actual
    // choice. Can happen with allow_non_choosable_default=true.
    rules.push((v) => {
      const isFromChoices = field.data.choices
        .map((choice) => choice.value)
        .includes(v);
      return isFromChoices || i18n.t('pleaseChooseOption');
    });
  } else if (isRangeField(field)) {
    rules.push((v) => v >= field.schema.minimum || i18n.t('tooSmall'));
    rules.push((v) => v <= field.schema.maximum || i18n.t('tooBig'));
    if (field.schema.multipleOf) {
      rules.push(
        (v) =>
          !field.schema.multipleOf ||
          (v * 100) % (field.schema.multipleOf * 100) === 0 ||
          i18n
            .t('mustBeMultipleOf')
            .replace('{{ value }}', field.schema.multipleOf)
      );
    }
  }
  return rules;
}

/**
 * Replace any comma in the value with a dot.
 *
 * E.g.: '5,99' -> '5.99'
 */
export function ensureDotSeparator(numberOrString) {
  if (numberOrString === null) {
    return numberOrString;
  }
  return String(numberOrString).replace(',', '.');
}

/**
 * Return trailing decimals after dot as string.
 *
 * Example input -> output:
 *   15.05 -> '0.5'
 *   999.0253 -> '0253'
 *   123 -> ''
 */
function getDecimals(number) {
  const normalized = ensureDotSeparator(number);
  if (normalized === null) {
    return '';
  }
  const parts = normalized.split('.');
  return parts[1] || '';
}

/**
 * Return number of trailing decimals after dot.
 *
 * Example input -> output:
 *   15.05 -> 2
 *   999.0253 -> 4
 *   123 -> 0
 */
export function getNumDecimals(number) {
  return getDecimals(number).length;
}

/**
 * Check if two numbers have compatible number of decimals.
 *
 * This is meant to check an input number e.g. a price against the
 * field.schema.multipleOf (which usually is either 1, 0.1 or 0.01).
 *
 * If the multipleOf says we need one decimals (via 0.1), then two
 * decimals are not allowed, however zero decimals are okay and so on.
 *
 * We also ignore decimals if the number ends with all zeros like 1.00.
 */
function hasEqualOrLessOrZeroValueDecimals(number1, number2) {
  const decimals1 = getDecimals(number1);
  const decimals2 = getDecimals(number2);

  if (decimals1 == '' && decimals2 == '') {
    return true;
  }
  if (Number(decimals1) === 0 && Number(decimals2) === 0) {
    return true;
  }
  return decimals1.length <= decimals2.length;
}

export function getDefaultOverlayDataForOverlayOptions(overlayOptions) {
  if (!overlayOptions) {
    return {};
  }

  const staticFieldsBlock = overlayOptions.static_fields || {};
  let dataBlock = null;
  if (overlayOptions.dynamic_fields && overlayOptions.dynamic_fields.data) {
    dataBlock = overlayOptions.dynamic_fields.data;
  }
  if (!staticFieldsBlock && !dataBlock) {
    return {};
  }

  const overlayData = {};
  // Add static_fields first, so defaults may still override it.
  for (let fieldName of Object.keys(staticFieldsBlock)) {
    overlayData[fieldName] = staticFieldsBlock[fieldName];
  }
  for (let fieldName of Object.keys(dataBlock)) {
    const defaultValue = dataBlock[fieldName].default_value;
    overlayData[fieldName] = defaultValue;
  }
  return overlayData;
}

/**
 * Return object mapping brandable field name to field data block.
 *
 * A field is 'brandable' when its data block includes:
 *
 *   from_branding: '<branding-attribute>'
 *
 * Returns an object (must iterate Object.keys() to handle):
 *
 *   {'<fieldname>': '<fieldDataBlock>', ...}
 *
 */
export function getBrandableFieldsFromOverlayOptions(overlayOptions) {
  if (!overlayOptions) {
    return {};
  }

  let dataBlock = null;
  if (overlayOptions.dynamic_fields && overlayOptions.dynamic_fields.data) {
    dataBlock = overlayOptions.dynamic_fields.data;
  }
  if (!dataBlock) {
    return {};
  }

  const brandableFields = {};
  for (let fieldName of Object.keys(dataBlock)) {
    const fieldData = dataBlock[fieldName];
    if (fieldData.from_branding) {
      brandableFields[fieldName] = fieldData;
    }
  }
  return brandableFields;
}

/**
 * Modify overlayData in-place to get values from Branding.
 *
 * This only applies to fields marked via '.from_branding' and when the
 * Branding has a value for it.
 */
export function applyBrandingToOverlayDataBrandableFields(
  branding,
  overlayOptions,
  overlayData
) {
  const brandableFields = getBrandableFieldsFromOverlayOptions(overlayOptions);
  for (const fieldName of Object.keys(brandableFields)) {
    const valueFromBranding = branding[fieldName];
    if (valueFromBranding === undefined) {
      continue;
    }
    overlayData[fieldName] = valueFromBranding;
  }
}

/**
 * Return object mapping field name to default data.
 *
 * It will only include fields where 'default_value_must_be_changed: true'.
 *
 * Note: IMAGE fields default to 'true' for this attribute, since e.g.
 *   producing with stock photo images usually is not allowed.
 *
 * This can be used to detect when the User did not change a
 * default_value so we can show a warning etc.
 *
 * Returns:
 *
 *   {
 *     '<fieldname>': {
 *       'default_value_must_be_changed': Boolean,
 *       'default_value': Any,
 *       'kind': String,
 *       'label': String,
 *     }
 *   }
 */
export function getFieldsWhereDefaultMustBeChanged(overlayOptions) {
  if (!overlayOptions) {
    return {};
  }

  let dataBlock = null;
  if (overlayOptions.dynamic_fields && overlayOptions.dynamic_fields.data) {
    dataBlock = overlayOptions.dynamic_fields.data;
  }
  if (!dataBlock) {
    return {};
  }

  const nameToDefaultInfo = {};
  for (let fieldName of Object.keys(dataBlock)) {
    const fieldData = dataBlock[fieldName];
    const fallback = fieldData.kind === 'IMAGE' ? true : false;
    const mustBeChanged =
      fieldData.default_value_must_be_changed === undefined
        ? fallback
        : fieldData.default_value_must_be_changed;
    if (!mustBeChanged) {
      continue;
    }
    nameToDefaultInfo[fieldName] = {
      default_value_must_be_changed: mustBeChanged,
      default_value: fieldData.default_value,
      kind: fieldData.kind,
      label: fieldData.label,
    };
  }
  return nameToDefaultInfo;
}

// Mixin
export default {
  data() {
    return {
      editableTemplate_: null,
      overlayOptions_: null,
    };
  },
  computed: {
    /**
     * We can pull the overlayOptions from two places:
     *
     * - Either a private 'overlayOptions_' helper which is where the
     *   data is stored after it has been fetched explicitly, e.g. in
     *   the edit dialog.
     *
     * - From the 'this.spot' data attribute of a component that uses
     *   this mixin, e.g. the SpotDetail page.
     *
     * There is no need to fetch the data if it is already present in
     * the component's local data.
     */
    overlayOptions() {
      if (this.overlayOptions_ !== null) {
        return this.overlayOptions_;
      }
      if (
        this.spot &&
        this.spot.ctype === 'tv' &&
        this.spot.overlay_options &&
        Object.keys(this.spot.overlay_options).length > 0
      ) {
        return this.spot.overlay_options;
      }
      return null;
    },
    /**
     * Same as with overlayOptions, try to use local spot state or fetch it.
     */
    editableTemplate() {
      // Use  a spot-specific template (e.g. for 9:16), if available.
      if (
        this.spot &&
        this.spot.ctype === 'tv' &&
        this.spot.editable_template
      ) {
        return this.spot.editable_template;
      }
      // Otherwise take the cached bundle template (e.g. for 16:9), if available.
      if (this.editableTemplate_ !== null) {
        return this.editableTemplate_;
      }
      return null;
    },
    hasOverlayOptions() {
      return this.overlayOptions !== null;
    },
    overlayOptionsGroups() {
      return this.overlayOptions.dynamic_fields.groups || [];
    },
    overlayOptionsAreGrouped() {
      return this.overlayOptionsGroups.length > 0;
    },
    overlayOptionsVisibleGroups() {
      return this.overlayOptionsGroups.filter(
        (group) => !group.hide_during_activation
      );
    },
  },
  methods: {
    /**
     * If overlay_options and editable_template cannot be taken from the
     * local context we fetch it based on the bundle here and store it
     * locally.
     */
    fetchOverlayInformationForTVSpotBundleId(bundleId) {
      return this.$api.retrieveContent('tv', bundleId).then((response) => {
        const bundle = response.data;
        const mainTVSpot = getMainTVSpotFromBundle(bundle);
        this.overlayOptions_ = mainTVSpot.overlay_options;
        this.editableTemplate_ = mainTVSpot.editable_template;
      });
    },
    updateOverlayDataForEntryIdent(entryIdent, overlayData) {
      const payload = {
        overlay_data: overlayData,
      };
      return this.$api.batchUpdateEntries([entryIdent], payload);
    },
    resetOverlayOptions() {
      this.overlayOptions_ = null;
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/overlayOptions.js